// Part-of-speech tokens are stored in the DB as single camelCase words
// (e.g. "StativeVerb", "ProperNoun", "AdverbAuxiliary"), with multiple parts of
// speech separated by " ; " (and joined across a word's senses with " · ").
// This turns that raw string into readable, de-duplicated, categorised tokens
// so the UI can render one coloured chip per part of speech.

// Broad families that share a chip colour. Related parts of speech (e.g. "Verb"
// and "Stative Verb") intentionally land in the same category so the palette
// stays small and a given part of speech is always the same colour.
export type PosCategory =
   | 'noun'
   | 'verb'
   | 'adjective'
   | 'adverb'
   | 'pronoun'
   | 'function'
   | 'interjection'
   | 'numeral'
   | 'other'

export type PosToken = {
   label: string
   category: PosCategory
}

// Split a camelCase / PascalCase token into spaced words, e.g.
// "StativeVerb" -> "Stative Verb", "ProperNoun" -> "Proper Noun". Runs of
// capitals (acronyms like "NEG") are left intact.
const spaceCamelCase = (token: string): string =>
   token
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .trim()

// Keyword → category, checked in order (first match wins). Order matters:
// "ProperNoun" must hit 'noun', "StativeVerb" must hit 'verb', etc.
const CATEGORY_RULES: [RegExp, PosCategory][] = [
   [/verb/i, 'verb'],
   [/noun/i, 'noun'],
   [/adjective/i, 'adjective'],
   [/adverb/i, 'adverb'],
   [/pronoun/i, 'pronoun'],
   [/preposition|conjunction|particle|determiner|marker|preceding|intensifier|neg\b/i, 'function'],
   [/interjection|onomatopoeia|rhyme|repeater/i, 'interjection'],
   [/classifier|quantifier|numeral|number/i, 'numeral'],
]

const categorise = (label: string): PosCategory => {
   for (const [pattern, category] of CATEGORY_RULES) {
      if (pattern.test(label)) {
         return category
      }
   }
   return 'other'
}

// Normalise a single raw token into a display label, or null when it carries no
// information (e.g. the literal "None").
const formatToken = (raw: string): string | null => {
   const trimmed = raw.trim()
   if (!trimmed || /^none$/i.test(trimmed)) {
      return null
   }
   const spaced = spaceCamelCase(trimmed)
   // Fully-lowercase legacy tokens (e.g. "verb") get a leading capital; already
   // capitalised / acronym tokens are left as authored.
   return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Parse a raw part-of-speech string into ordered, de-duplicated chips.
export const parsePartOfSpeech = (raw: string | null | undefined): PosToken[] => {
   if (!raw) {
      return []
   }
   const seen = new Set<string>()
   const tokens: PosToken[] = []
   for (const piece of raw.split(/[;·,/]/)) {
      const label = formatToken(piece)
      if (!label) {
         continue
      }
      const key = label.toLowerCase()
      if (seen.has(key)) {
         continue
      }
      seen.add(key)
      tokens.push({ label, category: categorise(label) })
   }
   return tokens
}
